import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import CVBuilder from './components/cv-builder/CVBuilder';
import PrintCvPage from './components/cv-builder/PrintCvPage';
import TemplateGallery from './components/template-gallery/TemplateGallery';
import DocumentHub from './components/dashboard/DocumentHub';
import LetterGallery from './components/letter-builder/LetterGallery';
import LetterWizard from './components/letter-builder/LetterWizard';
import LetterEditor from './components/letter-builder/LetterEditor';
import PrintLetterPage from './components/letter-builder/PrintLetterPage';
import Login from './components/auth/Login';
import AdminLogin from './components/auth/AdminLogin';
import Register from './components/auth/Register';
import LandingPage from './components/LandingPage/LandingPage';
import AdminDashboard from './components/admin/AdminDashboard';
import AnnouncementHost from './components/AnnouncementHost';
import SupportNotifications from './components/SupportNotifications';
import ReportProblemFab from './components/ReportProblemFab';
import ContentProtection from './components/ContentProtection';
import { useAuth, useAdminAuth, isAdminAppPath } from './context/AuthContext';
import { trackDailyVisit } from './utils/visitTracker';
import { trackPageView } from './utils/analytics';

const UserLoginRoute: React.FC = () => {
  const { isAuthenticated } = useAuth();
  if (isAuthenticated) return <Navigate to="/espace" replace />;
  return <Login />;
};

const AdminLoginRoute: React.FC = () => {
  const { isAuthenticated } = useAdminAuth();
  if (isAuthenticated) return <Navigate to="/bo-flashcv" replace />;
  return <AdminLogin />;
};

const AdminAppRoute: React.FC = () => {
  const { isAuthenticated } = useAdminAuth();
  if (!isAuthenticated) return <Navigate to="/bo-flashcv/login" replace />;
  return <AdminDashboard />;
};

const AppChrome: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isPrint =
    location.pathname.startsWith('/print/') || location.pathname.startsWith('/print-lettre/');

  React.useEffect(() => {
    // Statistiques de fréquentation du site public — on exclut le backoffice
    // pour ne pas comptabiliser les visites de l'administrateur lui-même.
    if (!isPrint && !isAdminAppPath(location.pathname)) {
      trackDailyVisit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    // page_view GA4 à chaque changement de route (SPA — gtag ne le fait pas
    // automatiquement, send_page_view est désactivé dans index.html).
    // Mêmes exclusions que le suivi de fréquentation ci-dessus.
    if (!isPrint && !isAdminAppPath(location.pathname)) {
      trackPageView(location.pathname + location.search);
    }
  }, [isPrint, location.pathname, location.search]);

  if (isPrint) return <>{children}</>;
  return (
    <>
      <ContentProtection />
      <AnnouncementHost />
      <SupportNotifications />
      <ReportProblemFab />
      {children}
    </>
  );
};

const App: React.FC = () => {
  const { isAuthenticated, loading: userLoading } = useAuth();
  const { loading: adminLoading } = useAdminAuth();
  const isPrintPath =
    typeof window !== 'undefined' &&
    (window.location.pathname.startsWith('/print/') ||
      window.location.pathname.startsWith('/print-lettre/'));

  // Page print (Puppeteer) : ne pas bloquer sur le chargement auth
  if (!isPrintPath && (userLoading || adminLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-600" />
      </div>
    );
  }

  return (
    <div translate="no">
      <Router>
        <AppChrome>
          <Routes>
            <Route path="/" element={<LandingPage />} />

            {/* Portail utilisateur */}
            <Route path="/login" element={<UserLoginRoute />} />
            <Route
              path="/register"
              element={!isAuthenticated ? <Register /> : <Navigate to="/espace" replace />}
            />
            {/* Hub post-connexion : choix CV / lettre de motivation */}
            <Route
              path="/espace"
              element={isAuthenticated ? <DocumentHub /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/choix-modele"
              element={isAuthenticated ? <TemplateGallery /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/builder"
              element={isAuthenticated ? <CVBuilder /> : <Navigate to="/login" replace />}
            />
            {/* Lettres de motivation — branche feature isolée */}
            <Route
              path="/creer-lettre"
              element={isAuthenticated ? <LetterWizard /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/choix-modele-lettre"
              element={isAuthenticated ? <LetterGallery /> : <Navigate to="/login" replace />}
            />
            <Route
              path="/lettre/:id"
              element={isAuthenticated ? <LetterEditor /> : <Navigate to="/login" replace />}
            />
            {/* Impression serveur (ticket JWT) — public */}
            <Route path="/print/:cvId" element={<PrintCvPage />} />
            <Route path="/print-lettre/:letterId" element={<PrintLetterPage />} />

            {/* Portail admin (URL non liée depuis le site public) */}
            <Route path="/bo-flashcv/login" element={<AdminLoginRoute />} />
            <Route path="/bo-flashcv" element={<AdminAppRoute />} />
            <Route path="/bo-flashcv/*" element={<AdminAppRoute />} />

            {/* Anciennes URLs → nouveau portail admin (sans lien public) */}
            <Route path="/admin" element={<Navigate to="/bo-flashcv" replace />} />
            <Route path="/admin/*" element={<Navigate to="/bo-flashcv" replace />} />
            <Route path="/login/admin" element={<Navigate to="/bo-flashcv/login" replace />} />
            {/* Ancien portail (avant renommage FastCV → FlashCV) */}
            <Route path="/bo-fastcv" element={<Navigate to="/bo-flashcv" replace />} />
            <Route path="/bo-fastcv/login" element={<Navigate to="/bo-flashcv/login" replace />} />
            <Route path="/bo-fastcv/*" element={<Navigate to="/bo-flashcv" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AppChrome>
      </Router>
    </div>
  );
};

export default App;
